import type { SalesFollowupTask } from '@/services/sales'
import { withServiceInstanceId } from '@/lib/service-instance'

export const SALES_FOLLOWUP_ACTIVITY_LABELS: Record<string, string> = {
  site_visit: 'Site Visit',
  call: 'Call',
  meeting: 'Meeting',
  quote_prep: 'Quote Prep',
  site_review: 'Site Review',
  contract: 'Contract',
  general: 'General',
}

export const SALES_FOLLOWUP_PRIORITY_CLASSES: Record<string, string> = {
  high: 'border-rose-300 bg-rose-50 text-rose-700',
  medium: 'border-amber-300 bg-amber-50 text-amber-700',
  low: 'border-slate-200 bg-slate-50 text-slate-600',
}

export type FollowupTreeNode = SalesFollowupTask & {
  children: FollowupTreeNode[]
}

export function formatSalesFollowupDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function buildFollowupTree(tasks: SalesFollowupTask[]): FollowupTreeNode[] {
  const nodes = new Map<number, FollowupTreeNode>()
  for (const task of tasks) {
    nodes.set(task.id, { ...task, children: [] })
  }

  const roots: FollowupTreeNode[] = []
  for (const task of tasks) {
    const node = nodes.get(task.id)
    if (!node) continue
    if (task.parent_followup_id && nodes.has(task.parent_followup_id)) {
      nodes.get(task.parent_followup_id)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  const sortNodes = (items: FollowupTreeNode[]) => {
    items.sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0
      return aTime - bTime
    })
    for (const item of items) {
      sortNodes(item.children)
    }
  }

  sortNodes(roots)
  return roots
}

export function getRootFollowupByEnquiry(tasks: SalesFollowupTask[]) {
  const map: Record<number, SalesFollowupTask> = {}
  for (const task of tasks) {
    if (task.enquiry_id != null && task.parent_followup_id == null) {
      map[task.enquiry_id] = task
    }
  }
  return map
}

export function buildEnquiryFollowupsPath(serviceInstanceId: string | null, enquiryId: number) {
  return withServiceInstanceId(`/sales/enquiries/${enquiryId}/follow-ups`, serviceInstanceId)
}

export function toDateTimeLocalValue(iso: string | null | undefined) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
